import React from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Group,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { 
    IconSearch, 
    IconUserPlus,
    IconUsers, 
    IconTrash
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useElementSize } from '@mantine/hooks';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { 
    usePatientDelegations, 
    useRevokeDelegation,
    useAvailableHcps
} from '../../../global/hooks/useHcpDelegation.tsx';
import { 
    ConsentStatus, 
    IDelegationModel
} from '../../../global/apis/HcpDelegationApi/HcpDelegationApi';
import HcpDelegationForm from '../../HcpDashboard/Form/HcpDelegationForm';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { theme } from '../../../global/style/theme.tsx';
import { IListItem } from '../../../global/apis/SexesApi/SexesApi.ts';

export type IPatientDelegationsProps = {
    patientStore?: PatientStore;
};
 
const PatientDelegations: React.FC<IPatientDelegationsProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { t } = useTranslation();
        
        const patientId = patientStore?.patientId ? parseInt(patientStore.patientId) : 0;
        
        const { data: delegations, isLoading } = usePatientDelegations(patientId);
        const { data: availableHcps } = useAvailableHcps();
        const revokeDelegation = useRevokeDelegation();
        const { setSortedData, sortedData, sortBy, reverseSortDirection, setSorting, search, setSearch } = useTable();
        const { ref } = useElementSize();

        React.useEffect(() => {
            setSortedData([]);
            if (delegations?.length) 
                setSortedData(delegations);
            
        }, [delegations, setSortedData]);

        const handleRevokeDelegation = (delegation: IDelegationModel) => {
            modals.openConfirmModal({
                title: t('delegation.revoke.title'),
                children: (
                    <Stack gap="sm">
                        <Text>{t('delegation.revoke.confirmation')}</Text>
                        <Text size="sm" c="dimmed">
                            {delegation.delegatedHcpName}
                        </Text>
                    </Stack>
                ),
                labels: { confirm: t('delegation.revoke.confirm'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                onConfirm: () => revokeDelegation.mutate({ delegationId: delegation.id }),
            });
        };

        const handleCreateDelegation = () => {
            if (!availableHcps || availableHcps.length === 0) {
                modals.open({
                    title: t('delegation.create.noHcpsTitle'),
                    children: (
                        <Text>{t('delegation.create.noHcpsMessage')}</Text>
                    ),
                });
                return;
            }

            modals.open({
                title: (<Group>
                            <IconUserPlus size={24} />
                            <Title order={3}>{t('delegation.create.title')}</Title>
                        </Group>),
                size: 'xl', // Increased size for the new table layout
                children: <HcpDelegationForm 
                    patientID={patientId}
                    onSuccess={() => modals.closeAll()}
                />,
            });
        };

        const getConsentStatusBadge = (status: ConsentStatus) => {
            const statusConfig = {
                [ConsentStatus.Pending]: { color: 'yellow', label: t('delegation.status.pending') },
                [ConsentStatus.Granted]: { color: 'green', label: t('delegation.status.granted') },
                [ConsentStatus.Revoked]: { color: 'red', label: t('delegation.status.revoked') },
                [ConsentStatus.Expired]: { color: 'gray', label: t('delegation.status.expired') },
            };

            const config = statusConfig[status];
            return <Badge color={config.color} size="sm">{config.label}</Badge>;
        };

        if (isLoading) {
            return (
                <Paper p="md">
                    <Text>{t('global.loading')}</Text>
                </Paper>
            );
        }

        return (
            <Stack gap="md">
                <Card withBorder p="md">
                    <Group justify="space-between" mb="md">
                        {patientStore?.name && (
                            <Text fw={500} size="lg">
                                {patientStore.name}
                                {patientStore.patientId && (
                                    <Text span c="dimmed" size="sm" ml="xs">
                                        (ID: {patientStore.patientId})
                                    </Text>
                                )}
                            </Text>
                        )}
                        <Button
                            leftSection={<IconUserPlus size={16} />}
                            onClick={handleCreateDelegation}
                        >
                            {t('delegation.create.addDelegation')}
                        </Button>
                    </Group>

                    {delegations && delegations.length > 0 ? (
                        <Paper ref={ref} withBorder>
                            <Stack p="md">
                                <TextInput
                                    placeholder={t('global.search')}
                                    rightSection={<IconSearch size={16} />}
                                    value={search}
                                    onChange={(el) => setSearch(el.currentTarget.value)}
                                />
                                
                                <ScrollArea>
                                    <Table>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Th
                                                    sorted={sortBy === 'delegatedHcpName'}
                                                    reversed={reverseSortDirection}
                                                    onSort={() => setSorting('delegatedHcpName', sortedData)}
                                                >
                                                    {t('delegation.table.hcpName')}
                                                </Th>
                                                <Th
                                                    sorted={sortBy === 'delegatedHcpSpecialty'}
                                                    reversed={reverseSortDirection}
                                                    onSort={() => setSorting('delegatedHcpSpecialty', sortedData)}
                                                    visibleFrom={theme.breakpoints?.sm}
                                                >
                                                    {t('delegation.table.specialty')}
                                                </Th>
                                                <Th
                                                    sorted={sortBy === 'patientConsentStatus'}
                                                    reversed={reverseSortDirection}
                                                    onSort={() => setSorting('patientConsentStatus', sortedData)}
                                                    visibleFrom={theme.breakpoints?.md}
                                                >
                                                    {t('delegation.table.consentStatus')}
                                                </Th>
                                                <Th
                                                    sorted={sortBy === 'createdDate'}
                                                    reversed={reverseSortDirection}
                                                    onSort={() => setSorting('createdDate', sortedData)}
                                                    visibleFrom={theme.breakpoints?.lg}
                                                >
                                                    {t('delegation.table.createdDate')}
                                                </Th>
                                                <Table.Th w={80}>
                                                    {t('global.actions')}
                                                </Table.Th>
                                            </Table.Tr> 
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {sortedData?.map((delegation: IDelegationModel, index) => {
                                                const isSearch = containsStringIgnoreCase(
                                                    [delegation.delegatedHcpName, delegation.delegatedHcpSpecialty.toString()],
                                                    search
                                                ) || !search;

                                                return (
                                                    <Table.Tr
                                                        key={delegation.id}
                                                        style={{
                                                            background: index % 2 === 1 
                                                                ? 'var(--Background-Gray-light, #E9ECEF)' 
                                                                : undefined,
                                                        }}
                                                        hidden={!isSearch}
                                                    >
                                                        <Table.Td>
                                                            <Text fw={500}>{delegation.delegatedHcpName}</Text>
                                                        </Table.Td>
                                                        <Table.Td visibleFrom={theme.breakpoints?.sm}>
                                                            <Text size="sm" c="dimmed">
                                                                {delegation.healthcareProfessionalTypes.find((type: IListItem) => type.id === delegation.delegatedHcpSpecialty)?.name || 'Unknown Specialty'}
                                                            </Text>
                                                        </Table.Td>
                                                        <Table.Td visibleFrom={theme.breakpoints?.md} style={{ whiteSpace: 'nowrap', minWidth: '90px' }}>
                                                            {getConsentStatusBadge(delegation.patientConsentStatus)}
                                                        </Table.Td>
                                                        <Table.Td visibleFrom={theme.breakpoints?.lg}>
                                                            <Text size="sm">
                                                                {new Date(delegation.createdDate).toLocaleDateString()}
                                                            </Text>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {delegation.canRevoke && (
                                                                <Tooltip label={t('delegation.revoke.tooltip')}>
                                                                    <ActionIcon
                                                                        variant="subtle"
                                                                        color="red"
                                                                        onClick={() => handleRevokeDelegation(delegation)}
                                                                        loading={revokeDelegation.isPending}
                                                                    >
                                                                        <IconTrash size={18} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </Table.Td>
                                                    </Table.Tr>
                                                );
                                            })}
                                        </Table.Tbody>
                                    </Table>
                                </ScrollArea>
                            </Stack>
                        </Paper>
                    ) : (
                        <Paper p="xl" ta="center">
                            <Stack align="center" gap="md">
                                <IconUsers size={48} color="var(--mantine-color-gray-5)" />
                                <div>
                                    <Text fw={500} mb="xs">{t('delegation.patient.noDelegations')}</Text>
                                    <Text size="sm" c="dimmed">
                                        {t('delegation.patient.noDelegationsHint')}
                                    </Text>
                                </div>
                            </Stack>
                        </Paper>
                    )}
                </Card>
            </Stack>
        );
    }),
);

const WrappedPatientDelegations: React.FC = () => {
    return (
        <TableContextProvider>
            <PatientDelegations />
        </TableContextProvider>
    );
};

export default WrappedPatientDelegations;
